import { useRouter } from "next/router";
import { useEffect, useRef } from "react";

import { AnimatePresence } from "@enkor/framer-motion";
import { ImpressionArea, useToggleState } from "@enkor/toss-slash";
import { useQueryClient } from "@enkor/react-query";
import { formatDate, ignoreTimezone, parseISO } from "@enkor/date-fns";

import { useFetchMyProjectListInfiniteQuery } from "@domains/project/network/projectQueries";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { getColorButtonAnimation } from "@domains/common/utils/animationHelper";
import { pink } from "@enkor/design/colors";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import useWebStorage, { STORAGE_KEY } from "@logics/hooks/useWebStorage";
import useOutsideClick from "@logics/hooks/useOutsideClick";
import { authQueryKeys } from "@domains/auth/constants/authQueryKeys";
import { removeAuthTokens } from "@domains/auth/utils/authTokenHandler";
import { myPageQueryKeys } from "@domains/myPage/constants/myPageQueryKeys";

import * as S from "./ProjectList.style";

const PROJECT_LIST_LIMIT = 3;

const ProjectList = () => {
  const router = useRouter();

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const [isMenuOpen, toggleMenu] = useToggleState();

  const { user } = useAuthSession();

  const [, setRedirectUrl] = useWebStorage(
    STORAGE_KEY.AUTH_REDIRECT,
    "",
    "localStorage",
  );

  const {
    data: projectList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchMyProjectListInfiniteQuery({
    limit: PROJECT_LIST_LIMIT,
  });

  const handleImpressionStart = () => {
    if (!hasNextPage || isFetchingNextPage) return;

    fetchNextPage();
  };

  const handleLogin = () => {
    router.push(NAVIGATION_ROUTE.ADMIN_LOGIN.HREF);
  };

  const handleLogout = () => {
    removeAuthTokens();

    queryClient.removeQueries({
      queryKey: myPageQueryKeys.getProfile(),
    });
    queryClient.removeQueries({
      queryKey: authQueryKeys.getUserProfile(),
    });

    window.localStorage.setItem(STORAGE_KEY.AUTH_TRIGGERED, "false");

    router.push(NAVIGATION_ROUTE.ADMIN_PROJECT_LIST.HREF);

    toggleMenu();
    ToastSuccess("로그아웃 되었습니다.");
  };

  useOutsideClick(menuRef, (event) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      toggleMenu();
    }
  });

  const handleCreateClick = () => {
    if (!user) {
      ToastError("로그인 후 이용해주세요.");
      router.push(NAVIGATION_ROUTE.ADMIN_LOGIN.HREF);
    } else {
      router.push(NAVIGATION_ROUTE.ADMIN_PROJECT_CREATE.HREF);
    }
  };

  const handleEditClick = (projectId: string | number) => {
    if (!user) {
      ToastError("로그인 후 이용해주세요.");
      router.push(NAVIGATION_ROUTE.ADMIN_LOGIN.HREF);
    } else {
      router.push({
        pathname: NAVIGATION_ROUTE.ADMIN_PROJECT_UPDATE.PATHNAME,
        query: { projectId },
      });
    }
  };

  const handleAddEventClick = (projectId: string | number) => {
    router.push({
      pathname: NAVIGATION_ROUTE.ADMIN_POST_PROJECT_EVENT.HREF,
      query: {
        projectId,
      },
    });
  };

  useEffect(() => {
    setRedirectUrl(NAVIGATION_ROUTE.ADMIN_PROJECT_LIST.HREF);
  }, []);

  return (
    <S.Container>
      {user ? (
        <>
          <S.UserInfoContainer ref={triggerRef} onClick={toggleMenu}>
            <S.ProfileImage
              src={user.url || "/images/default-profile.png"}
              alt="프로필 이미지"
            />
            <S.UserName name="strongM">{user.nickName}</S.UserName>
          </S.UserInfoContainer>

          <AnimatePresence>
            {isMenuOpen && (
              <S.UserDropdown
                ref={menuRef}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <S.DropdownItem onClick={handleLogout}>로그아웃</S.DropdownItem>
              </S.UserDropdown>
            )}
          </AnimatePresence>
        </>
      ) : (
        <S.LoginButton
          onClick={handleLogin}
          {...getColorButtonAnimation({
            hoverColor: pink[300],
            tapColor: pink[200],
          })}
        >
          로그인
        </S.LoginButton>
      )}

      <S.HeaderContainer>
        <S.Title>에디션 목록</S.Title>
        <S.CreateButton aria-label="프로젝트 생성" onClick={handleCreateClick}>
          생성하기
        </S.CreateButton>
      </S.HeaderContainer>

      <S.NoticeWrapper>
        <S.NoticeText name="l">에디션 관련 확인 사항</S.NoticeText>
        <S.NoticeText name="strongM">
          - 에디션 목록을 확인하려면 로그인이 필요합니다. 에디션을 관리할
          계정으로 로그인해주세요.
        </S.NoticeText>
        <S.NoticeText name="strongM">
          - 에디션 생성 및 수정은 앱이 아닌 웹에서만 가능합니다.
        </S.NoticeText>
        <S.NoticeText name="strongM">
          - 에디션 생성 후 꼭 이벤트 생성해주셔야 합니다.
        </S.NoticeText>
        <S.NoticeText name="strongM">
          - 에디션 채팅 이벤트는 가장 먼저 시작하는 이벤트입니다.
        </S.NoticeText>
        <S.NoticeText name="strongM">
          - 활성화 해야만 홈 화면에서 에디션을 확인할 수 있습니다.
        </S.NoticeText>
        <S.NoticeText name="strongM">
          - 에디션 삭제는 아직 없으며 에디션을 내리고 싶으면 비활성화 해주세요.
        </S.NoticeText>
      </S.NoticeWrapper>

      <S.ProjectList>
        {projectList?.map((project) => (
          <S.ProjectItem key={project.id}>
            <S.ProjectContent>
              <S.ProjectTitle>{project.title}</S.ProjectTitle>
              <S.ProjectMeta>
                <S.ProjectMetaItem>
                  <S.ProjectMetaLabel>시작일:</S.ProjectMetaLabel>
                  {formatDate({
                    date: parseISO(ignoreTimezone(project.startDate)),
                    customType: "yyyy.MM.dd",
                  })}
                </S.ProjectMetaItem>
                {project.availableSpots && (
                  <S.ProjectMetaItem>
                    <S.ProjectMetaLabel>자리:</S.ProjectMetaLabel>
                    {project.availableSpots}
                  </S.ProjectMetaItem>
                )}
              </S.ProjectMeta>
            </S.ProjectContent>
            <S.EditButtonContainer>
              <S.EditButton
                aria-label="프로젝트 수정"
                onClick={() => handleEditClick(project.id)}
              >
                에디션 수정하기
              </S.EditButton>
              <S.AddEventButton
                aria-label="이벤트 추가"
                onClick={() => handleAddEventClick(project.id)}
              >
                이벤트 추가하기
              </S.AddEventButton>
            </S.EditButtonContainer>
          </S.ProjectItem>
        ))}
      </S.ProjectList>

      {projectList && !isFetchingNextPage && hasNextPage && (
        <ImpressionArea onImpressionStart={handleImpressionStart} />
      )}
    </S.Container>
  );
};

export default ProjectList;
